import { useState, useEffect, useCallback } from 'react';
import {
  Plus, X, CheckCircle2, Send,
  FileText, Calendar, UserCheck, Tag,
} from 'lucide-react';
import { FolderOpen } from 'lucide-react';
import SearchableSelect from './SearchableSelect';
import NoDppDropdown from './NoDppDropdown';
import { allCategories, iconMap } from '../../../helpers/Project-Report/data/allCategories';
import { avatarColors } from '../../../helpers/Project-Report/utils/helpers';
import { createProject, fetchPIC, fetchPICByKeyword, fetchProjectCategoriesSummary, fetchTbUserByNik, getUserByToken } from '../../../services/projectReportServices';
import { useLocalStorage } from 'react-use';
import { mergeCategoriesData } from '../../../helpers/Project-Report/utils/mergeCategoriesData';

const emptyForm = {
  projectName: '',
  descriptionProject: '',
  projectNo: '',
  pic: '',
  targetDate: '',
  dateStart: '',
  dateEnd: '',
  categoryId: null,
  createdBy: null,
  updatedBy: null,
  UserNikCreated: null,
};

const CreateProjectModal = ({ isOpen, onClose, defaultCategory, accentColor, accentGradient, onSuccess, nik = null }) => {
  const [form, setForm] = useState(emptyForm);
  const [token, _] = useLocalStorage('token', '');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Controlled open state untuk kedua dropdown — hanya satu bisa terbuka sekaligus
  const [ownerOpen, setOwnerOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [pic, setPic] = useState([]);

  const fetchUser = async () => {
    const userToken = token;
    const response = await (nik ? fetchTbUserByNik(nik) : getUserByToken(userToken));
    if (response && (response.status === true || response.status === 'success' || response.status === 200)) {
      const userData = Array.isArray(response.data) ? response.data[0] : response.data;
      const userName = userData?.nama_lengkap || userData?.nama_user;
      setForm((prevForm) => ({
        ...prevForm,
        createdBy: userName,
        updatedBy: userName,
        UserNikCreated: userData?.nik || null,
      }));
    }
  };
  const fetchpic = async () => {
    try {
      const response = await fetchPIC();
      const formattedPic = response.map((p, idx) => ({
        ...p,
        id: parseInt(p.NIK, 10) || idx,
        name: p.nama,
        department: p.departemen || '',
        avatar: p.nama ? p.nama.substring(0, 2).toUpperCase() : 'U'
      }));
      setPic(formattedPic);
    } catch (error) {
      console.error('Error fetching PIC:', error);
    }
  };

  const handlePicSearch = useCallback(async (keyword) => {
    try {
      const response = await fetchPICByKeyword(keyword);
      const formattedPic = response.map((p, idx) => ({
        ...p,
        id: parseInt(p.NIK, 10) || idx,
        name: p.nama,
        department: p.departemen || '',
        avatar: p.nama ? p.nama.substring(0, 2).toUpperCase() : 'U'
      }));
      setPic(formattedPic);
    } catch (error) {
      console.error('Error searching PIC:', error);
    }
  }, []);

  const projectCategories = async () => {
    const result = await fetchProjectCategoriesSummary();
            
    // Merge API data with dummy data (descriptions, icons, etc)
    const mergedCategories = mergeCategoriesData(result.categories, allCategories);
    setCategories(mergedCategories);
  }

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setForm({ ...emptyForm, categoryId: defaultCategory?.id || null });
      if (token || nik) fetchUser();
      fetchpic();
      projectCategories();
      setErrors({});
      setSubmitted(false);
    }
  }, [isOpen, defaultCategory?.id]);

  const validate = () => {
    const e = {};
    if (!form.projectName.trim()) e.projectName = 'Nama proyek wajib diisi';
    if (!form.projectNo.trim()) e.projectNo = 'Nomor proyek wajib diisi';
    if (!form.dateStart) e.dateStart = 'Tanggal mulai wajib diisi';
    if (!form.dateEnd) e.dateEnd = 'Tanggal selesai wajib diisi';
    if (!form.pic) e.pic = 'PIC wajib dipilih';
    if (!form.categoryId) e.categoryId = 'Kategori wajib dipilih';
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSubmitting(true);
    try {
      // Destructure picObj out of the form data to prevent sending it to the API
      const { picObj, ...submitData } = form;
      await createProject(submitData);
      setSubmitting(false);
      setSubmitted(true);
      if (onSuccess) onSuccess(); // Melakukan refetch data terbaru di ProjectList
      
      setTimeout(() => {
        // Reset form ke emptyForm tapi handle fetchUser untuk memastikan token createdBy ada
        setForm(emptyForm);
        if (token || nik) fetchUser(); // Pastikan user di set ulang
        onClose();
      }, 1200);
    } catch (err) {
      console.error('Gagal membuat proyek:', err);
      setSubmitting(false);
      alert(err.response?.data?.message || 'Gagal membuat proyek. Silakan coba lagi.');
    }
  };

  if (!isOpen) return null;

  // ── Field label helper ──
  const Label = ({ icon: Icon, text, required, error }) => (
    <div style={{ marginBottom: '6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', fontWeight: '600', color: error ? '#ef4444' : '#374151' }}>
        <Icon size={13} color={error ? '#ef4444' : accentColor} />
        {text}{required && <span style={{ color: '#ef4444', marginLeft: '2px' }}>*</span>}
      </div>
      {error && <span style={{ fontSize: '11px', color: '#ef4444' }}>{error}</span>}
    </div>
  );

  // ── Input style helper ──
  const inputStyle = (hasErr) => ({
    width: '100%',
    padding: '10px 14px',
    borderRadius: '10px',
    border: hasErr ? '1.5px solid #ef4444' : '1.5px solid #e2e8f0',
    background: '#fafafa',
    fontSize: '13px',
    color: '#0f172a',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
    fontFamily: "inherit",
  });

  return (
    <>
      {/* ── Backdrop ── */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(15,23,42,0.55)',
          backdropFilter: 'blur(4px)',
          animation: 'backdropIn 0.2s ease',
        }}
      />

      {/* ── Modal panel ── */}
      <div
        style={{
          position: 'fixed',
          top: '50%', left: '50%',
          transform: 'translate(-50%,-50%)',
          zIndex: 1001,
          width: '560px',
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc(100vh - 48px)',
          overflowY: 'auto',
          background: '#fff',
          borderRadius: '20px',
          boxShadow: '0 32px 80px rgba(0,0,0,0.25)',
          animation: 'modalIn 0.25s cubic-bezier(0.34,1.56,0.64,1)',
          fontFamily: "'Inter', sans-serif",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div
          style={{
            padding: '24px 28px 20px',
            borderBottom: '1px solid #f1f5f9',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            background: '#fff',
            zIndex: 5,
            borderRadius: '20px 20px 0 0',
          }}
        >
          <div>
            <div
              style={{
                width: '40px', height: '40px', borderRadius: '12px',
                background: accentGradient,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: '10px',
                boxShadow: `0 4px 14px ${accentColor}40`,
              }}
            >
              <Plus size={20} color="#fff" strokeWidth={2.5} />
            </div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0f172a', letterSpacing: '-0.3px' }}>
              Buat Project Baru
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: '12.5px', color: '#94a3b8' }}>
              Lengkapi informasi project di bawah ini
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: '32px', height: '32px', borderRadius: '50%',
              border: '1.5px solid #e2e8f0', background: '#f8fafc',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', transition: 'all 0.2s', flexShrink: 0,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.borderColor = '#fca5a5'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
          >
            <X size={14} color="#64748b" />
          </button>
        </div>

        {/* ── Body ── */}
        {submitted ? (
          // Success state
          <div style={{ padding: '48px 28px', textAlign: 'center' }}>
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <CheckCircle2 size={32} color="#22c55e" />
            </div>
            <h3 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>Proyek Berhasil Dibuat!</h3>
            <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>Proyek "<strong>{form.projectName}</strong>" telah ditambahkan.</p>
          </div>
        ) : (
          <div style={{ padding: '24px 28px' }}>

            {/* Project Name */}
            <div style={{ marginBottom: '18px' }}>
              <Label icon={FileText} text="Nama Project" required error={errors.projectName} />
              <input
                type="text"
                placeholder="Masukkan nama project..."
                value={form.projectName}
                onChange={(e) => { setForm({ ...form, projectName: e.target.value }); setErrors({ ...errors, projectName: '' }); }}
                style={inputStyle(errors.projectName)}
                onFocus={(e) => { e.target.style.borderColor = accentColor; e.target.style.boxShadow = `0 0 0 3px ${accentColor}20`; }}
                onBlur={(e) => { e.target.style.borderColor = errors.projectName ? '#ef4444' : '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
              />
            </div>

            {/* Project No */}
            <div style={{ marginBottom: '18px' }}>
              <Label icon={FileText} text="Nomor Project" required error={errors.projectNo} />
              <NoDppDropdown
                value={form.projectNo}
                onChange={(val) => { setForm({ ...form, projectNo: val }); setErrors({ ...errors, projectNo: '' }); }}
                accent={accentColor}
                placeholder="Pilih nomor project..."
                cellInputStyle={inputStyle(errors.projectNo)}
              />
            </div>

            {/* Date Range */}
            <div style={{ marginBottom: '18px' }}>
              <Label icon={Calendar} text="Rentang Tanggal" required error={errors.dateStart || errors.dateEnd} />
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <input
                    type="date"
                    value={form.dateStart}
                    onChange={(e) => { setForm({ ...form, dateStart: e.target.value }); setErrors({ ...errors, dateStart: '' }); }}
                    style={{ ...inputStyle(errors.dateStart), paddingRight: '10px' }}
                    onFocus={(e) => { e.target.style.borderColor = accentColor; e.target.style.boxShadow = `0 0 0 3px ${accentColor}20`; }}
                    onBlur={(e) => { e.target.style.borderColor = errors.dateStart ? '#ef4444' : '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                  />
                </div>
                <span style={{ fontSize: '13px', color: '#94a3b8', flexShrink: 0 }}>→</span>
                <div style={{ flex: 1 }}>
                  <input
                    type="date"
                    value={form.dateEnd}
                    min={form.dateStart}
                    onChange={(e) => { setForm({ ...form, dateEnd: e.target.value }); setErrors({ ...errors, dateEnd: '' }); }}
                    style={{ ...inputStyle(errors.dateEnd), paddingRight: '10px' }}
                    onFocus={(e) => { e.target.style.borderColor = accentColor; e.target.style.boxShadow = `0 0 0 3px ${accentColor}20`; }}
                    onBlur={(e) => { e.target.style.borderColor = errors.dateEnd ? '#ef4444' : '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                  />
                </div>
              </div>
            </div>

            {/* Description */}
            <div style={{ marginBottom: '18px' }}>
              <Label icon={FileText} text="Deskripsi Project" />
              <textarea
                placeholder="Tuliskan deskripsi singkat project..."
                rows={3}
                value={form.descriptionProject}
                onChange={(e) => setForm({ ...form, descriptionProject: e.target.value })}
                style={{ ...inputStyle(false), resize: 'vertical', minHeight: '80px', lineHeight: '1.6' }}
                onFocus={(e) => { e.target.style.borderColor = accentColor; e.target.style.boxShadow = `0 0 0 3px ${accentColor}20`; }}
                onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
              />
            </div>

            {/* PIC (Project Owner) */}
            <div style={{ marginBottom: '18px' }}>
              <Label icon={UserCheck} text="PIC" required error={errors.pic} />
              <SearchableSelect
                placeholder="Silakan Pilih..."
                items={pic}
                value={
                  // p.NIK + p.name sebagai unique identifier
                  pic.find((p) => `${p.NIK}-${p.name}` === form.pic) || 
                  (form.picObj ? form.picObj : null) // Mempertahankan objek agar tetap tampil ketika list hilang
                }
                onChange={(val) => { 
                  setForm({ ...form, pic: `${val.NIK}-${val.name}-${val.department}-${val.bagian}`, picObj: val }); 
                  setErrors({ ...errors, pic: '' }); 
                }}
                accentColor={accentColor}
                isOpen={ownerOpen}
                onToggle={() => { setOwnerOpen((p) => !p); setCategoryOpen(false); }}
                onClose={() => setOwnerOpen(false)}
                onSearch={handlePicSearch}
                renderSelected={(v) => (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: avatarColors[v.id % avatarColors.length], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: '700', color: '#fff', flexShrink: 0 }}>{v.avatar}</div>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#0f172a' }}>{v.name}</span>
                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>— {v.NIK} - {v.department}</span>
                  </div>
                )}
                renderItem={(item, isActive, color) => (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: avatarColors[item.id % avatarColors.length], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700', color: '#fff', flexShrink: 0, boxShadow: '0 2px 6px rgba(0,0,0,0.12)' }}>{item.avatar}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: isActive ? '700' : '600', color: isActive ? color : '#0f172a' }}>{item.name}</div>
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>{item.NIK} - {item.department}</div>
                    </div>
                    {isActive && <CheckCircle2 size={14} color={color} />}
                  </div>
                )}
              />
              {errors.pic && <span style={{ fontSize: '11px', color: '#ef4444', marginTop: '4px', display: 'block' }}>{errors.pic}</span>}
            </div>

            {/* Category */}
            <div style={{ marginBottom: '8px' }}>
              <Label icon={Tag} text="Kategori" required error={errors.categoryId} />
              <SearchableSelect
                placeholder="Pilih kategori proyek..."
                items={categories}
                value={categories.find((cat) => cat.id === form.categoryId) || null}
                onChange={(val) => { setForm({ ...form, categoryId: val.id }); setErrors({ ...errors, categoryId: '' }); }}
                accentColor={accentColor}
                isOpen={categoryOpen}
                onToggle={() => { setCategoryOpen((p) => !p); setOwnerOpen(false); }}
                onClose={() => setCategoryOpen(false)}
                renderSelected={(v) => {
                  const IC = iconMap[v.icon] || FolderOpen;
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '22px', height: '22px', borderRadius: '6px', background: v.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <IC size={12} color="#fff" strokeWidth={2} />
                      </div>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#0f172a' }}>{v.name}</span>
                    </div>
                  );
                }}
                renderItem={(item, isActive, color) => {
                  const IC = iconMap[item.icon] || FolderOpen;
                  const pct = item.progress;
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: isActive ? item.gradient : item.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background 0.2s', boxShadow: isActive ? `0 3px 8px ${item.color}40` : 'none' }}>
                        <IC size={16} color="#fff" strokeWidth={1.8} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px', marginBottom: '3px' }}>
                          <span style={{ fontSize: '13px', fontWeight: isActive ? '700' : '600', color: isActive ? item.color : '#0f172a' }}>{item.name}</span>
                          <span style={{ fontSize: '10px', fontWeight: '700', color: '#fff', background: `${item.color}cc`, padding: '1px 7px', borderRadius: '20px', flexShrink: 0 }}>{pct}%</span>
                        </div>
                        <div style={{ height: '3px', background: '#f1f5f9', borderRadius: '99px', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: item.gradient, borderRadius: '99px' }} />
                        </div>
                      </div>
                      {isActive && <CheckCircle2 size={14} color={item.color} style={{ flexShrink: 0 }} />}
                    </div>
                  );
                }}
              />

              {errors.categoryId && <span style={{ fontSize: '11px', color: '#ef4444', marginTop: '4px', display: 'block' }}>{errors.categoryId}</span>}
            </div>
          </div>
        )}

        {/* ── Footer ── */}
        {!submitted && (
          <div
            style={{
              padding: '16px 28px 24px',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
              borderTop: '1px solid #f1f5f9',
              position: 'sticky',
              bottom: 0,
              background: '#fff',
              borderRadius: '0 0 20px 20px',
            }}
          >
            <button
              onClick={onClose}
              style={{
                padding: '10px 22px', borderRadius: '10px',
                border: '1.5px solid #e2e8f0', background: '#f8fafc',
                fontSize: '13px', fontWeight: '600', color: '#64748b',
                cursor: 'pointer', transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
            >
              Batal
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={{
                padding: '10px 26px', borderRadius: '10px',
                border: 'none', background: submitting ? '#94a3b8' : accentGradient,
                fontSize: '13px', fontWeight: '700', color: '#fff',
                cursor: submitting ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: '8px',
                boxShadow: submitting ? 'none' : `0 4px 14px ${accentColor}50`,
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => { if (!submitting) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = `0 8px 20px ${accentColor}60`; } }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = `0 4px 14px ${accentColor}50`; }}
            >
              {submitting ? (
                <><span style={{ width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />Menyimpan...</>
              ) : (
                <><Send size={14} strokeWidth={2.5} />Submit</>  
              )}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CreateProjectModal;
